import { Card, CardHeader, CardTitle, CardAction, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, AlertTriangle, HardDrive, Database, UserPen, GitMerge, History, CircleHelp, Ban } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MigrationType } from "../models/Statistics/MigrationType";
import { MigrationFlagStatus } from "../models/Flags/MigrationFlagStatus";
import { FLAG_STATUS_SEARCH_PARAM } from "../components/common/Table/FlagStatusFilter";
import CasesStatisticsCard from "../components/Organization/CasesStatisticsCard";
import FirmDocumentsStatisticsCard from "../components/Organization/FirmDocumentsStatisticsCard";
import IntakesStatisticsCard from "../components/Organization/IntakesStatisticsCard";
import OverallStatisticsCard from "../components/Organization/OverallStatisticsCard";
import ProvidersStatisticsCard from "../components/Organization/ProvidersStatisticsCard";
import TemplatesStatisticsCard from "../components/Organization/TemplatesStatisticsCard";
import { OrgConflictResolveView } from "../components/Organization/OrgConflictResolveView";
import { OrgNotFoundRetryView } from "../components/Organization/OrgNotFoundRetryView";
import { OrganizationWithStatistics } from "../models/OrganizationWithStatistics";
import { rollUpVersioning } from "../models/ClientConfiguration/VersioningState";
import GraphLibrariesRepository from "../repositories/GraphLibrariesRepository";
import OrganizationsRepository from "../repositories/OrganizationsRepository";
import UsersRepository from "../repositories/UsersRepository";
import CasesRepository from "../repositories/CasesRepository";
import IntakesRepository from "../repositories/IntakesRepository";
import ProvidersRepository from "../repositories/ProvidersRepository";
import TemplatesRepository from "../repositories/TemplatesRepository";
import FirmDocumentsFoldersRepository from "../repositories/FirmDocumentsFoldersRepository";
import { User, UserRole } from "../models/User";
import GenericAutocomplete from "../components/common/GenericAutocomplete";
import { convertUtcToLocal, roundToTwoDecimalPoints } from "../helpers/CommonHelpers";
import { calculateFilesStatistics } from "../models/Statistics/MigrationStatistics";
import { QUERY_KEYS } from "../constants/QueryKeys";
import { useAuth } from "../contexts/AuthContext";

/** The types the Flagged tile breaks down, in caption order, with the list each one links to. */
const FLAGGED_TILE_TYPES: { type: MigrationType; one: string; many: string; listPath: string }[] = [
  { type: MigrationType.CasesMigration, one: "case", many: "cases", listPath: "cases" },
  { type: MigrationType.IntakesMigration, one: "intake", many: "intakes", listPath: "intakes" },
  { type: MigrationType.ProvidersMigration, one: "provider", many: "providers", listPath: "providers" },
  { type: MigrationType.FirmDocumentsMigration, one: "firm docs folder", many: "firm docs folders", listPath: "firm-documents-folders" },
  { type: MigrationType.TemplatesMigration, one: "template library", many: "template libraries", listPath: "templates" },
];

const FLAG_TILE_STATE_CAPTIONS = {
  "no-access": "needs document access",
  loading: "checking…",
  unavailable: "could not be loaded",
} as const;

interface OrganizationPageProps {
  organizationId: string;
  userRole: UserRole;
  isAssignedUser: boolean;
  hasDocumentAccess: boolean;
}

const OrganizationPage: React.FC<OrganizationPageProps> = ({
  organizationId,
  userRole,
  isAssignedUser,
  hasDocumentAccess,
}: OrganizationPageProps) => {
  const [organization, setOrganization] = useState<OrganizationWithStatistics | undefined>(undefined);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<{ id: string; label: string } | undefined>(undefined);
  const currentUserId = useAuth().getUserId();
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [displayStatisticsAccordingToMigrationStrategy, setDisplayStatisticsAccordingToMigrationStrategy] = useState(true);
  const queryClient = useQueryClient();
  const organizationsRepository = new OrganizationsRepository();
  const usersRepository = new UsersRepository();
  const casesRepository = new CasesRepository();
  const intakesRepository = new IntakesRepository();
  const providersRepository = new ProvidersRepository();
  const templatesRepository = new TemplatesRepository();
  const firmDocumentsFoldersRepository = new FirmDocumentsFoldersRepository();
  const graphLibrariesRepository = new GraphLibrariesRepository();

  const organizationQuery = useQuery({
    queryKey: [QUERY_KEYS.ORGANIZATION, organizationId],
    queryFn: async () => await organizationsRepository.getOrganizationWithStatistics(organizationId),
    gcTime: 2 * 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
    refetchOnMount: true,
  });

  // Every Editor and Admin, not only those already assigned somewhere: the filter list on Organizations shares
  // ASSIGNABLE_USERS with that narrower meaning, so this picker keeps a key of its own.
  const userQuery = useQuery({
    queryKey: [QUERY_KEYS.ASSIGNABLE_USERS, "all"],
    queryFn: async () =>
      await usersRepository.getUsers({
        roles: [UserRole.Admin, UserRole.Editor],
        fetchAll: true,
        descending: false,
        orderBy: "username",
      }),
    gcTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchOnMount: false,
  });

  // Entity-level conflict counts for the KPI card — one cheap pagination-count call per family (pageSize 1
  // returns just the total in the x-pagination header). "Has conflicts" is the stat-based filter (an entity
  // with >=1 unresolved conflict), so this stays consistent with the recalc-lagged Flagged card.
  const conflictEntityCountsQuery = useQuery({
    queryKey: [QUERY_KEYS.ORGANIZATION, organizationId, "conflict-entity-counts"],
    queryFn: async () => {
      const totalOf = (r: { paging: { totalCount: number } | null }) => r.paging?.totalCount ?? 0;
      const [cases, intakes, providers, templates, firmDocs] = await Promise.all([
        casesRepository.getCases({ hasConflicts: true, pageNumber: 1, pageSize: 1 }),
        intakesRepository.getIntakes({ hasConflicts: true, pageNumber: 1, pageSize: 1 }),
        providersRepository.getProviders({ hasConflicts: true, pageNumber: 1, pageSize: 1 }),
        templatesRepository.getTemplates({ hasConflicts: true, pageNumber: 1, pageSize: 1 }),
        firmDocumentsFoldersRepository.getFirmDocumentsFolders({ hasConflicts: true, pageNumber: 1, pageSize: 1 }),
      ]);
      return {
        cases: totalOf(cases),
        intakes: totalOf(intakes),
        providers: totalOf(providers),
        templates: totalOf(templates),
        firmDocs: totalOf(firmDocs),
      };
    },
    gcTime: 2 * 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
    refetchOnMount: true,
  });

  // Read from the entities' flags, not the statistics: an entity's statistics status is Flagged only while none of its
  // files has uploaded or ended Not Found, so a block placed after uploads began would never be counted. Needs
  // document access like the lists the tiles link to, so it is not asked for without it.
  const flagCountsQuery = useQuery({
    queryKey: [QUERY_KEYS.ORG_FLAG_COUNTS, organizationId],
    queryFn: async () => await organizationsRepository.getFlagCounts(),
    enabled: hasDocumentAccess,
    gcTime: 2 * 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
    refetchOnMount: true,
  });

  // Same key as the client-configurations table, so the tile's count and that table's per-client
  // column share one fetch and cannot disagree.
  const versioningQuery = useQuery({
    queryKey: [QUERY_KEYS.ORGANIZATION_GRAPH_LIBRARY_VERSIONING, organizationId],
    queryFn: async () => await graphLibrariesRepository.getOrganizationVersioningStatus(),
    gcTime: 2 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  const versioningRollup = useMemo(
    () => (versioningQuery.data ? rollUpVersioning(versioningQuery.data) : null),
    [versioningQuery.data],
  );

  /**
   * Versioning tile presentation. "Nothing established" is deliberately its own neutral state rather
   * than being folded into the healthy one: no observation is not evidence of compliance, and painting
   * it green would report an unchecked organization as clean.
   */
  const versioningTile = useMemo(() => {
    if (!versioningRollup) {
      return { value: "…", caption: "checking…", tone: "muted" as const };
    }

    const { versionedCount, consideredCount, undeterminedCount } = versioningRollup;

    if (versionedCount > 0) {
      return {
        value: `${versionedCount}`,
        caption: `${versioningRollup.affectedConfigurationCount} of ${versioningRollup.configurationCount} clients affected`,
        tone: "warning" as const,
      };
    }

    if (consideredCount === 0) {
      return { value: "0", caption: "no libraries registered", tone: "muted" as const };
    }

    if (undeterminedCount === consideredCount) {
      return { value: "0", caption: "nothing checked yet", tone: "muted" as const };
    }

    return { value: "0", caption: "all libraries off", tone: "success" as const };
  }, [versioningRollup]);

  useEffect(() => {
    if (organizationQuery.data) {
      setOrganization(organizationQuery.data);
      setSelectedUser(
        organizationQuery.data.assignedUser
          ? {
              id: organizationQuery.data.assignedUser.id,
              label: organizationQuery.data.assignedUser.username,
            }
          : undefined,
      );
    }

    if (userQuery.data) setUsers(userQuery.data.data);
  }, [userQuery.data, organizationQuery.data, organizationId]);

  const displayOpenCaseAndIntakeStatistics = useMemo(() => {
    return !organization
      ? false
      : !displayStatisticsAccordingToMigrationStrategy || organization.displayOpenedCaseAndIntakeStatistics;
  }, [displayStatisticsAccordingToMigrationStrategy, organization]);

  const displayClosedCaseAndIntakeStatistics = useMemo(() => {
    return !organization
      ? false
      : !displayStatisticsAccordingToMigrationStrategy || organization.displayClosedCaseAndIntakeStatistics;
  }, [displayStatisticsAccordingToMigrationStrategy, organization]);

  const kpiStats = useMemo(() => {
    if (!organization) return null;
    const stats = organization.migrationStatistics;
    const { casesStatistics, intakesStatistics, providersStatistics, firmDocumentFoldersStatistics, templateFilesStatistics } = stats;
    const open = displayOpenCaseAndIntakeStatistics;
    const closed = displayClosedCaseAndIntakeStatistics;

    let totalFolders = casesStatistics.numberOfCaseFolders
      + providersStatistics.numberOfProviders
      + firmDocumentFoldersStatistics.numberOfFolders
      + templateFilesStatistics.numberOfTemplateLibraries;
    if (open) totalFolders += casesStatistics.numberOfOpenCases + intakesStatistics.numberOfOpenIntakes;
    if (closed) totalFolders += casesStatistics.numberOfClosedCases + intakesStatistics.numberOfClosedIntakes;

    let migratedFolders = casesStatistics.caseFoldersMigrated
      + providersStatistics.providersMigrated
      + firmDocumentFoldersStatistics.foldersMigrated
      + templateFilesStatistics.templateLibrariesMigrated;
    if (open) migratedFolders += casesStatistics.openCasesMigrated + intakesStatistics.openIntakesMigrated;
    if (closed) migratedFolders += casesStatistics.closedCasesMigrated + intakesStatistics.closedIntakesMigrated;

    const filesStats = calculateFilesStatistics(stats, open, closed);

    return {
      totalFolders,
      migratedFolders,
      totalSizeMb: filesStats.TotalFilesSizeInMb,
      migratedSizeMb: filesStats.MigratedFilesSizeInMb,
      totalSizeGb: filesStats.TotalFilesSizeInGb,
      migratedSizeGb: filesStats.MigratedFilesSizeInGb,
    };
  }, [organization, displayOpenCaseAndIntakeStatistics, displayClosedCaseAndIntakeStatistics]);

  const userOptions = users.map((user) => ({
    id: user.id,
    label: user.username,
  }));

  const { isLoading, isError } = organizationQuery;
  if (isLoading || isError || !!!organization) {
    return <></>;
  }

  const { intactId, name, sharepointSiteUrl, migrationStatistics } = organization as OrganizationWithStatistics;

  const {
    casesStatistics,
    intakesStatistics,
    providersStatistics,
    firmDocumentFoldersStatistics,
    templateFilesStatistics,
  } = migrationStatistics;

  const allocatedSpace = migrationStatistics.allocatedSharepointSpaceInGb
    ? roundToTwoDecimalPoints(migrationStatistics.allocatedSharepointSpaceInGb)
    : null;

  const usedSpace = migrationStatistics.usedSharepointSpaceInGb
    ? roundToTwoDecimalPoints(migrationStatistics.usedSharepointSpaceInGb)
    : null;

  const usedPercentage =
    usedSpace && allocatedSpace
      ? `${((parseFloat(usedSpace) / parseFloat(allocatedSpace)) * 100).toFixed(2)}%`
      : null;

  const handleSelectUser = async (selectedOption: { id: string; label: string }) => {
    setSelectedUser(selectedOption);
    try {
      await organizationsRepository.patchOrganizationAssignedUser(organizationId, selectedOption.id);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATIONS] }),
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION, organizationId] }),
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION_NAVIGATION_INFO, organizationId] }),
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ASSIGNABLE_USERS] }),
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USERS] }),
      ]);
      toast.success(`Assigned ${selectedOption.label} to ${name}`);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to update assigned user",
      );
    }
  };

  const handleRefreshStatistics = async () => {
    setIsRecalculating(true);
    try {
      await organizationsRepository.RecalculateOrganizationStatistics(organizationId);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION, organizationId] });
    } catch (error) {
      console.error(error);
    } finally {
      setIsRecalculating(false);
    }
  };

  const migrationPct = kpiStats && kpiStats.totalFolders > 0
    ? Math.round((kpiStats.migratedFolders / kpiStats.totalFolders) * 100)
    : 0;
  const sizePct = kpiStats && kpiStats.totalSizeMb > 0
    ? Math.round((kpiStats.migratedSizeMb / kpiStats.totalSizeMb) * 100)
    : 0;
  const useGbForKpi = (kpiStats?.totalSizeGb ?? 0) > 1;

  const conflictCounts = conflictEntityCountsQuery.data;
  const conflictEntities = conflictCounts
    ? conflictCounts.cases + conflictCounts.intakes + conflictCounts.providers + conflictCounts.templates + conflictCounts.firmDocs
    : 0;

  // Without document access the lists cannot be opened and the counts cannot be read, so the flag tiles say so
  // rather than showing a 0 that would read as "nothing held".
  const flagCounts = flagCountsQuery.data;
  const flagTileState = !hasDocumentAccess
    ? "no-access"
    : flagCountsQuery.isLoading ? "loading" : flagCounts ? "ready" : "unavailable";
  const blockedCount = (type: MigrationType) => flagCounts?.find((c) => c.migrationType === type)?.blocked ?? 0;
  const flaggedTotal = FLAGGED_TILE_TYPES.reduce((sum, t) => sum + blockedCount(t.type), 0);
  const caseFlags = flagCounts?.find((c) => c.migrationType === MigrationType.CasesMigration);
  const blockedCases = caseFlags?.blocked ?? 0;
  const blockedListPath = (listPath: string) =>
    `/organization/${organizationId}/${listPath}?${FLAG_STATUS_SEARCH_PARAM}=${MigrationFlagStatus.UnResolved}`;
  const blockedCasesCaption = (() => {
    if (!caseFlags) return "";
    const acknowledged = caseFlags.acknowledged > 0 ? `${caseFlags.acknowledged} acknowledged` : null;
    if (caseFlags.blocked === 0) return ["no blocked cases", acknowledged].filter(Boolean).join(" · ");
    const parts = [
      caseFlags.blockedOver10KFiles > 0 ? `${caseFlags.blockedOver10KFiles} > 10K Files` : null,
      caseFlags.blockedByUser > 0 ? `${caseFlags.blockedByUser} by user` : null,
      acknowledged,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(" · ") : "held out of upload";
  })();

  return (
    <div className="flex flex-col gap-6 flex-1 min-h-0 overflow-y-auto">
      {/* Page header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">{intactId} – {name}</h2>
        </div>
        <div className="flex items-center gap-3">
          {/* Both dialogs' counts and actions need document access, so without it they could only fail. */}
          {userRole !== UserRole.Viewer && hasDocumentAccess && (
            <>
              <OrgNotFoundRetryView organizationId={organizationId} />
              <OrgConflictResolveView organizationId={organizationId} />
            </>
          )}
          {userQuery.isLoading ? (
            <Skeleton className="h-10 w-[350px]" />
          ) : (
            <GenericAutocomplete
              // Only the current assigned user hands the organization on, or an Admin; temporary access is not enough.
              disabled={userRole === UserRole.Viewer
                || (organizationQuery.data?.assignedUser?.id !== currentUserId && userRole !== UserRole.Admin)}
              options={userOptions}
              inputValue={selectedUser ?? { id: "", label: "" }}
              setValue={handleSelectUser}
              placeholder="Assigned user"
              width="350px"
              startDecorator={
                <UserPen className="size-4 text-muted-foreground" />
              }
            />
          )}
        </div>
      </div>

      {/* KPI strip */}
      {kpiStats && (
        // One row only once each tile has room for its title and figures beside the sidebar: six tiles at 2xl, seven
        // (with SP Storage) from 1800px. Narrower, a tile's title and "migrated / total" wrap.
        <div className={cn(
          "grid gap-4 grid-cols-2 md:grid-cols-3",
          allocatedSpace && usedSpace ? "xl:grid-cols-4 min-[1800px]:grid-cols-7" : "2xl:grid-cols-6",
        )}>
          {/* Migrations */}
          <Card className="p-4 gap-0">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Migrations</p>
                <p className="text-2xl font-bold mt-1">
                  {kpiStats.migratedFolders}
                  <span className="text-sm font-normal text-muted-foreground"> / {kpiStats.totalFolders}</span>
                </p>
              </div>
              <div className="p-2 rounded-lg bg-primary/10">
                <CheckCircle2 className="h-4 w-4 text-primary" />
              </div>
            </div>
            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
              <div className="h-full rounded-full bg-primary transition-all duration-700 ease-out" style={{ width: `${migrationPct}%` }} />
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">{migrationPct}% migrated</p>
          </Card>

          {/* Flagged — entities of every type whose flag is Blocked; Acknowledged ones are a decision already taken. */}
          <Card className="p-4 gap-0">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Flagged</p>
                <p className={cn(
                  "text-2xl font-bold mt-1",
                  flagTileState !== "ready" ? "text-muted-foreground" : flaggedTotal > 0 ? "text-amber-500" : "text-green-600",
                )}>
                  {flagTileState === "ready" ? flaggedTotal : flagTileState === "loading" ? "…" : "—"}
                </p>
              </div>
              <div className={cn(
                "p-2 rounded-lg",
                flagTileState !== "ready" ? "bg-muted" : flaggedTotal > 0 ? "bg-amber-500/10" : "bg-green-500/10",
              )}>
                <AlertTriangle className={cn(
                  "h-4 w-4",
                  flagTileState !== "ready" ? "text-muted-foreground" : flaggedTotal > 0 ? "text-amber-500" : "text-green-600",
                )} />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              {flagTileState !== "ready"
                ? FLAG_TILE_STATE_CAPTIONS[flagTileState]
                : flaggedTotal === 0
                  ? "nothing flagged"
                  : (
                    <>
                      {FLAGGED_TILE_TYPES.filter((t) => blockedCount(t.type) > 0).map((t, i) => {
                        const n = blockedCount(t.type);
                        return (
                          <React.Fragment key={t.type}>
                            {i > 0 && " · "}
                            <Link to={blockedListPath(t.listPath)} className="underline-offset-2 hover:underline hover:text-foreground">
                              {n} {n === 1 ? t.one : t.many}
                            </Link>
                          </React.Fragment>
                        );
                      })}
                      {" awaiting a decision"}
                    </>
                  )}
            </p>
          </Card>

          {/* Conflicts */}
          <Card className="p-4 gap-0">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Conflicts</p>
                <p className={cn("text-2xl font-bold mt-1", conflictEntities > 0 ? "text-red-600 dark:text-red-400" : "text-green-600")}>
                  {conflictEntityCountsQuery.isLoading ? "…" : conflictEntities}
                </p>
              </div>
              <div className={cn("p-2 rounded-lg", conflictEntities > 0 ? "bg-red-500/10" : "bg-green-500/10")}>
                <GitMerge className={cn("h-4 w-4", conflictEntities > 0 ? "text-red-600 dark:text-red-400" : "text-green-600")} />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              {conflictEntityCountsQuery.isLoading
                ? "checking…"
                : conflictCounts && conflictEntities > 0
                  ? [
                      conflictCounts.cases > 0 ? `${conflictCounts.cases} cases` : null,
                      conflictCounts.intakes > 0 ? `${conflictCounts.intakes} intakes` : null,
                      conflictCounts.providers > 0 ? `${conflictCounts.providers} providers` : null,
                      conflictCounts.firmDocs > 0 ? `${conflictCounts.firmDocs} firm docs` : null,
                      conflictCounts.templates > 0 ? `${conflictCounts.templates} templates` : null,
                    ].filter(Boolean).join(" · ") + " have conflicts"
                  : "no conflicts"}
            </p>
          </Card>

          {/* Blocked Cases — opens the Cases list filtered to Blocked. */}
          {(() => {
            const tone = flagTileState !== "ready" ? "muted" : blockedCases > 0 ? "warning" : "success";
            const card = (
              <Card className={cn("p-4 gap-0 h-full", flagTileState === "ready" && "transition-colors hover:bg-accent/50")}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Blocked Cases</p>
                    <p className={cn(
                      "text-2xl font-bold mt-1",
                      tone === "muted" && "text-muted-foreground",
                      tone === "warning" && "text-amber-500",
                      tone === "success" && "text-green-600",
                    )}>
                      {flagTileState === "ready" ? blockedCases : flagTileState === "loading" ? "…" : "—"}
                    </p>
                  </div>
                  <div className={cn(
                    "p-2 rounded-lg",
                    tone === "muted" && "bg-muted",
                    tone === "warning" && "bg-amber-500/10",
                    tone === "success" && "bg-green-500/10",
                  )}>
                    <Ban className={cn(
                      "h-4 w-4",
                      tone === "muted" && "text-muted-foreground",
                      tone === "warning" && "text-amber-500",
                      tone === "success" && "text-green-600",
                    )} />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  {flagTileState === "ready" ? blockedCasesCaption : FLAG_TILE_STATE_CAPTIONS[flagTileState]}
                </p>
              </Card>
            );
            return flagTileState === "ready" ? (
              <Link
                to={blockedListPath("cases")}
                aria-label={`Blocked cases: ${blockedCases}. Open the Cases list filtered to Blocked.`}
                className="block rounded-xl outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
              >
                {card}
              </Link>
            ) : card;
          })()}

          {/* Files size */}
          <Card className="p-4 gap-0">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Files Size</p>
                <p className="text-2xl font-bold mt-1">
                  {useGbForKpi ? kpiStats.migratedSizeGb.toFixed(1) : kpiStats.migratedSizeMb.toFixed(1)}
                  <span className="text-sm font-normal text-muted-foreground">
                    {" / "}{useGbForKpi ? `${kpiStats.totalSizeGb.toFixed(1)} GB` : `${kpiStats.totalSizeMb.toFixed(1)} MB`}
                  </span>
                </p>
              </div>
              <div className="p-2 rounded-lg bg-violet-500/10">
                <HardDrive className="h-4 w-4 text-violet-500" />
              </div>
            </div>
            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
              <div className="h-full rounded-full bg-violet-500 transition-all duration-700 ease-out" style={{ width: `${sizePct}%` }} />
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">{sizePct}% transferred</p>
          </Card>

          {/* Versioning — unconditional, so the signal never disappears with an absent site URL or
              missing storage figures. */}
          <Card className="p-4 gap-0">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Versioning</p>
                <p className={cn(
                  "text-2xl font-bold mt-1",
                  versioningTile.tone === "warning" && "text-amber-500",
                  versioningTile.tone === "success" && "text-green-600",
                  versioningTile.tone === "muted" && "text-muted-foreground",
                )}>
                  {versioningTile.value}
                </p>
              </div>
              <div className={cn(
                "p-2 rounded-lg",
                versioningTile.tone === "warning" && "bg-amber-500/10",
                versioningTile.tone === "success" && "bg-green-500/10",
                versioningTile.tone === "muted" && "bg-muted",
              )}>
                {versioningTile.tone === "warning" ? (
                  <History className="h-4 w-4 text-amber-500" />
                ) : versioningTile.tone === "success" ? (
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                ) : (
                  <CircleHelp className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">{versioningTile.caption}</p>
          </Card>

          {/* SharePoint storage (conditional) */}
          {allocatedSpace && usedSpace && (
            <Card className="p-4 gap-0">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">SP Storage</p>
                  <p className="text-2xl font-bold mt-1">
                    {usedSpace}
                    <span className="text-sm font-normal text-muted-foreground"> / {allocatedSpace} GB</span>
                  </p>
                </div>
                <div className="p-2 rounded-lg bg-sky-500/10">
                  <Database className="h-4 w-4 text-sky-500" />
                </div>
              </div>
              <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                <div className="h-full rounded-full bg-sky-500 transition-all duration-700 ease-out"
                  style={{ width: usedPercentage ?? "0%" }} />
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">{usedPercentage} used</p>
            </Card>
          )}
        </div>
      )}

      {/* SharePoint URL (if no storage data, show as simple card) */}
      {sharepointSiteUrl && (
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">SharePoint</CardTitle>
          </CardHeader>
          <CardContent>
            <a
              href={sharepointSiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline break-all text-sm"
            >
              {sharepointSiteUrl}
            </a>
          </CardContent>
        </Card>
      )}

      {/* Statistics */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Statistics</CardTitle>
          <CardAction>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Switch
                  id="strategy-switch"
                  checked={displayStatisticsAccordingToMigrationStrategy}
                  onCheckedChange={setDisplayStatisticsAccordingToMigrationStrategy}
                />
                <Label htmlFor="strategy-switch" className="text-sm font-normal cursor-pointer">
                  Show statistics according to the migration strategy
                </Label>
              </div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="icon"
                      variant="outline"
                      disabled={isRecalculating}
                      onClick={handleRefreshStatistics}
                    >
                      <RefreshCw className={isRecalculating ? "animate-spin" : ""} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    Statistics from: {convertUtcToLocal(organization.lastStatisticsRefreshDate as string)}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </CardAction>
        </CardHeader>
        <CardContent className="pt-2">
          <div className="flex flex-col gap-4">
            <OverallStatisticsCard
              displayOpenCaseAndIntakeStatistics={displayOpenCaseAndIntakeStatistics}
              displayClosedCaseAndIntakeStatistics={displayClosedCaseAndIntakeStatistics}
              statistics={migrationStatistics}
            />
            <div className="grid grid-cols-1 2xl:grid-cols-2 gap-4">
              <CasesStatisticsCard
                displayOpenCaseStatistics={displayOpenCaseAndIntakeStatistics}
                displayClosedCaseStatistics={displayClosedCaseAndIntakeStatistics}
                statistics={casesStatistics}
              />
              <IntakesStatisticsCard
                displayOpenIntakeStatistics={displayOpenCaseAndIntakeStatistics}
                displayClosedIntakeStatistics={displayClosedCaseAndIntakeStatistics}
                statistics={intakesStatistics}
              />
              <ProvidersStatisticsCard statistics={providersStatistics} />
              <FirmDocumentsStatisticsCard statistics={firmDocumentFoldersStatistics} />
              <TemplatesStatisticsCard statistics={templateFilesStatistics} />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OrganizationPage;
